<template>
  <transition name="modal">
      <!-- No se cierra al hacer click fuera (evita perder lo que se está
           editando); solo con la X o Cancelar. -->
      <div v-if="showModal" ref="createOverlay" class="modal-overlay">
        <div
          class="modal-container"
          :class="[
            successState ? 'modal-container--success' : 'modal-container--lg',
            scopeStep ? 'modal-container--ask' : '',
          ]"
        >
        <!-- ── FORM STATE ──────────────────────────────────────────────── -->
        <template v-if="!successState">
          <div class="modal-header">
            <h3>
              <i :class="isCorrectingMode ? 'fas fa-pen-to-square' : 'fas fa-file-invoice-dollar'"></i>
              {{ modalTitle }}
            </h3>

            <button class="btn-close" @click="close" :disabled="isProcessing">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="modal-body">

            <!-- ── PASO CONFIRMACIÓN ─────────────────────────────────────── -->
            <!-- ── Paso 0: ¿entera o por habitación? ─────────────────────
                 Solo en reservas de varias. Va ANTES del formulario porque de esta
                 respuesta depende de quién son los datos fiscales que se piden
                 después: si la factura es de una habitación, son los de ESE huésped. -->
            <template v-if="scopeStep">
              <div class="scope-step">
                <p class="scope-step__lead">{{ $t('manualInvoiceModal.scope.lead') }}</p>

                <div class="type-grid type-grid--stack">
                  <button
                    type="button"
                    class="type-option"
                    :class="{ active: !form.room_instance_id }"
                    @click="form.room_instance_id = null"
                  >
                    <div class="type-option__icon"><i class="fas fa-layer-group"></i></div>
                    <div class="type-option__text">
                      <span class="type-option__name">{{ $t('manualInvoiceModal.scope.whole') }}</span>
                      <small class="type-option__sub">
                        {{ $t('manualInvoiceModal.scope.wholeHint', { n: billableRooms.rooms.length }) }}
                      </small>
                    </div>
                    <span v-if="!form.room_instance_id" class="type-option__badge">
                      <i class="fas fa-check"></i> {{ $t('manualInvoiceModal.recipientType.active') }}
                    </span>
                  </button>

                  <button
                    type="button"
                    class="type-option"
                    :class="{ active: !!form.room_instance_id }"
                    @click="elegirPorHabitacion"
                  >
                    <div class="type-option__icon"><i class="fas fa-door-open"></i></div>
                    <div class="type-option__text">
                      <span class="type-option__name">{{ $t('manualInvoiceModal.scope.perRoom') }}</span>
                      <small class="type-option__sub">{{ $t('manualInvoiceModal.scope.perRoomHint') }}</small>
                    </div>
                    <span v-if="form.room_instance_id" class="type-option__badge">
                      <i class="fas fa-check"></i> {{ $t('manualInvoiceModal.recipientType.active') }}
                    </span>
                  </button>
                </div>

                <div v-if="form.room_instance_id" class="scope-rooms">
                  <span class="scope-rooms__label">{{ $t('manualInvoiceModal.scope.pickRoom') }}</span>
                  <div class="scope-rooms__list">
                    <button
                      v-for="room in billableRooms.rooms"
                      :key="room.id"
                      type="button"
                      class="scope-room"
                      :class="{ active: form.room_instance_id === room.id }"
                      @click="form.room_instance_id = room.id"
                    >
                      <span class="scope-room__name">{{ room.label }}</span>
                      <span class="scope-room__amount">{{ billableRooms.currency }} {{ fmtRoom(room.lodging) }}</span>
                    </button>
                  </div>
                  <small class="scope-rooms__hint">{{ $t('manualInvoiceModal.scope.repeatHint') }}</small>
                </div>
              </div>
            </template>

            <template v-else-if="confirmStep">
              <div class="confirm-view">
                <div class="confirm-view__intro">
                  <div class="confirm-view__icon">
                    <i class="fas fa-circle-check"></i>
                  </div>
                  <div>
                    <strong>{{ $t('manualInvoiceModal.confirm.title') }}</strong>
                    <p>{{ $t('manualInvoiceModal.confirm.subtitle') }}</p>
                  </div>
                </div>

                <div class="confirm-rows">
                  <div class="confirm-row">
                    <span class="confirm-row__label">{{ $t('manualInvoiceModal.confirm.rowDocument') }}</span>
                    <strong class="confirm-row__value">{{ $t('manualInvoiceModal.confirm.documentValue') }}</strong>
                  </div>
                  <div class="confirm-row">
                    <span class="confirm-row__label">{{ $t('manualInvoiceModal.confirm.rowType') }}</span>
                    <span class="confirm-row__value">
                      {{ form.customer_type === 'company' ? $t('manualInvoiceModal.confirm.typeCompany') : $t('manualInvoiceModal.confirm.typeIndividual') }}
                    </span>
                  </div>
                  <div class="confirm-row">
                    <span class="confirm-row__label">
                      {{ form.customer_type === 'company' ? $t('manualInvoiceModal.confirm.labelCompanyName') : $t('manualInvoiceModal.confirm.labelName') }}
                    </span>
                    <span class="confirm-row__value">{{ form.customer_name }}</span>
                  </div>
                  <div v-if="form.customer_tax_id" class="confirm-row">
                    <span class="confirm-row__label">{{ taxIdConfig.label }}</span>
                    <span class="confirm-row__value">{{ form.customer_tax_id }}</span>
                  </div>
                  <div class="confirm-row">
                    <span class="confirm-row__label">{{ $t('manualInvoiceModal.confirm.rowCountry') }}</span>
                    <span class="confirm-row__value">{{ form.customer_country }}</span>
                  </div>
                  <div v-if="form.customer_email" class="confirm-row">
                    <span class="confirm-row__label">{{ $t('manualInvoiceModal.confirm.rowEmail') }}</span>
                    <span class="confirm-row__value">{{ form.customer_email }}</span>
                  </div>
                  <div v-if="form.customer_address || form.customer_city" class="confirm-row">
                    <span class="confirm-row__label">{{ $t('manualInvoiceModal.confirm.rowAddress') }}</span>
                    <span class="confirm-row__value">{{ confirmSummaryAddress }}</span>
                  </div>
                  <div v-if="form.payment_source.length" class="confirm-row">
                    <span class="confirm-row__label">{{ $t('manualInvoiceModal.confirm.rowPayment') }}</span>
                    <span class="confirm-row__value">{{ paymentSourceLabel }}</span>
                  </div>
                  <div v-if="form.invoice_notes" class="confirm-row">
                    <span class="confirm-row__label">{{ $t('manualInvoiceModal.confirm.rowNotes') }}</span>
                    <span class="confirm-row__value confirm-row__value--notes">{{ form.invoice_notes }}</span>
                  </div>
                </div>

                <div v-if="error" class="alert-error" style="margin-top:12px">
                  <i class="fas fa-circle-exclamation"></i>
                  <div>{{ error }}</div>
                </div>
              </div>
            </template>

            <!-- ── FORMULARIO ────────────────────────────────────────────── -->
            <template v-else><!-- formulario -->
            <div v-if="error" class="alert-error">
              <i class="fas fa-circle-exclamation"></i>
              <div>{{ error }}</div>
            </div>

            <div class="info-box" :class="isCorrectingMode ? 'info-box--warn' : ''">
              <i :class="isCorrectingMode ? 'fas fa-triangle-exclamation' : 'fas fa-circle-info'"></i>
              <div>
                <strong>{{
                  isCorrectingMode
                    ? $t('manualInvoiceModal.info.correctingTitle')
                    : $t('manualInvoiceModal.info.createTitle')
                }}</strong>
                <p class="mb-0">
                  <template v-if="isCorrectingMode"><span v-html="$t('manualInvoiceModal.info.correctingBody')"></span></template>
                  <template v-else>{{ $t('manualInvoiceModal.info.createBody') }}</template>
                </p>
                <small v-if="prefillSourceLabel">{{
                  prefillSourceLabel
                }}</small>
              </div>
            </div>

            <div v-if="isPrefillLoading" class="prefill-loading">
              <span class="btn-spinner btn-spinner--dark"></span>
              {{ $t('manualInvoiceModal.prefillLoading') }}
            </div>

            <!-- 🚨 LO PRIMERO de todo en una reserva de varias habitaciones: de esta
                 respuesta depende de quién son los datos fiscales que se escriben
                 debajo. Si se pregunta después, el alojamiento ya se facturó entero y
                 no queda nada que repartir. -->
            <section class="type-box">
              <span class="type-box__label">{{ $t('manualInvoiceModal.recipientType.label') }}</span>
              <div class="type-grid">
                <button
                  type="button"
                  class="type-option"
                  :class="{ active: form.customer_type === 'individual' }"
                  @click="setCustomerType('individual')"
                >
                  <div class="type-option__icon">
                    <i class="fas fa-user"></i>
                  </div>
                  <div class="type-option__text">
                    <span class="type-option__name">{{ $t('manualInvoiceModal.recipientType.individual') }}</span>
                    <small class="type-option__sub">{{ $t('manualInvoiceModal.recipientType.individualSub') }}</small>
                  </div>
                  <span v-if="form.customer_type === 'individual'" class="type-option__badge">
                    <i class="fas fa-check"></i> {{ $t('manualInvoiceModal.recipientType.active') }}
                  </span>
                </button>
                <button
                  type="button"
                  class="type-option"
                  :class="{ active: form.customer_type === 'company' }"
                  @click="setCustomerType('company')"
                >
                  <div class="type-option__icon">
                    <i class="fas fa-building"></i>
                  </div>
                  <div class="type-option__text">
                    <span class="type-option__name">{{ $t('manualInvoiceModal.recipientType.company') }}</span>
                    <small class="type-option__sub">{{ $t('manualInvoiceModal.recipientType.companySub') }}</small>
                  </div>
                  <span v-if="form.customer_type === 'company'" class="type-option__badge">
                    <i class="fas fa-check"></i> {{ $t('manualInvoiceModal.recipientType.active') }}
                  </span>
                </button>
              </div>
            </section>

            <!-- ── Cliente del fichero ───────────────────────────────────────
                 Antes había que teclear a mano una empresa que YA estaba en el
                 fichero de clientes. Aquí se busca, se vuelcan sus datos fiscales
                 en los campos de abajo y se guarda el ENLACE con su ficha
                 (cliente_id + invoice_recipient_id).
                 Los campos siguen siendo editables: corregir algo NO rompe el
                 enlace, y el botón de quitar devuelve el formulario a mano. -->
            <section class="client-pick">
              <span class="client-pick__label">{{ $t('manualInvoiceModal.client.label') }}</span>

              <div class="client-pick__body">
                <template v-if="selectedClient">
                  <div class="client-pick__card">
                    <span class="client-pick__avatar">{{ clientInitials(selectedClient) }}</span>
                    <span class="client-pick__identity">
                      <strong>{{ clientDisplayName(selectedClient) }}</strong>
                      <small>{{ clientFiscalSummary(selectedClient) }}</small>
                    </span>
                    <span class="client-pick__type">{{
                      selectedClientIsCompany
                        ? $t('invoiceList.create.company')
                        : $t('invoiceList.create.individual')
                    }}</span>
                    <button
                      type="button"
                      class="client-pick__clear"
                      :disabled="isProcessing"
                      @click="clearSelectedClient"
                    >
                      <i class="fas fa-xmark"></i> {{ $t('manualInvoiceModal.client.clear') }}
                    </button>
                  </div>
                  <small class="field-hint">{{ $t('manualInvoiceModal.client.linkedHint') }}</small>
                </template>

                <template v-else>
                  <div class="client-pick__search">
                    <i class="fas fa-magnifying-glass client-pick__search-icon"></i>
                    <input
                      v-model.trim="clientSearch"
                      type="search"
                      class="field-input client-pick__input"
                      :placeholder="$t('invoiceList.create.clientSearchPlaceholder')"
                      @focus="openClientMenu"
                      @input="queueClientSearch"
                      @keydown.esc.stop="closeClientMenu"
                    />
                    <span v-if="loadingClients" class="btn-spinner btn-spinner--dark client-pick__spinner"></span>

                    <div v-if="clientMenuOpen" class="client-pick__menu">
                      <button
                        v-for="client in clients"
                        :key="client.id"
                        type="button"
                        class="client-pick__option"
                        @click="selectClient(client)"
                      >
                        <span class="client-pick__avatar client-pick__avatar--sm">{{ clientInitials(client) }}</span>
                        <span class="client-pick__identity">
                          <strong>{{ clientDisplayName(client) }}</strong>
                          <small>{{ clientFiscalSummary(client) }}</small>
                        </span>
                        <span v-if="!client.billing_profile" class="client-pick__badge">{{
                          $t('invoiceList.create.incomplete')
                        }}</span>
                      </button>
                      <p v-if="!loadingClients && !clients.length" class="client-pick__empty">
                        {{ $t('invoiceList.create.noClients') }}
                      </p>
                    </div>
                  </div>
                  <small class="field-hint">{{ $t('manualInvoiceModal.client.help') }}</small>
                </template>

                <!-- Cliente sin perfil fiscal: se avisa en vez de dejar emitir con
                     los datos fiscales vacíos (y sin enlazar su ficha). -->
                <div v-if="clientFiscalWarning" class="client-pick__warn">
                  <i class="fas fa-triangle-exclamation"></i>
                  <span>{{ clientFiscalWarning }}</span>
                </div>
              </div>
            </section>

            <div class="manual-grid">
              <div class="field field--full">
                <label>{{
                  form.customer_type === "company"
                    ? $t('manualInvoiceModal.fields.nameCompany')
                    : $t('manualInvoiceModal.fields.nameIndividual')
                }}</label>
                <input
                  v-model.trim="form.customer_name"
                  type="text"
                  class="field-input"
                />
                <small v-if="fieldErrors.customer_name" class="field-error">{{
                  fieldErrors.customer_name
                }}</small>
              </div>

              <div class="field field--full">
                <label>{{ $t('manualInvoiceModal.fields.country') }}</label>
                <div
                  class="multiselect-wrap"
                  :class="{
                    'multiselect-wrap--error': fieldErrors.customer_country,
                  }"
                >
                  <multiselect
                    v-model="selectedCountryOption"
                    :options="countryOptions"
                    :searchable="true"
                    :allow-empty="true"
                    :show-labels="false"
                    track-by="code"
                    label="label"
                    :placeholder="$t('manualInvoiceModal.fields.countryPlaceholder')"
                    @input="onCountrySelected"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">
                      <span>{{ option.label }}</span>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                      <div class="country-option">
                        <span>{{ option.label }}</span>
                        <small>{{ option.code }}</small>
                      </div>
                    </template>
                  </multiselect>
                </div>
                <small v-if="!fieldErrors.customer_country" class="field-hint">
                  {{ $t('manualInvoiceModal.fields.countryHint') }}
                </small>
                <small
                  v-if="fieldErrors.customer_country"
                  class="field-error"
                  >{{ fieldErrors.customer_country }}</small
                >
              </div>

              <div class="field">
                <label>
                  {{ taxIdConfig.label }}
                  <span v-if="taxIdConfig.required">*</span>
                  <span v-else class="field-optional">{{ $t('manualInvoiceModal.fields.optional') }}</span>
                </label>
                <input
                  v-model.trim="form.customer_tax_id"
                  type="text"
                  class="field-input"
                  @input="normalizeTaxIdInput"
                  @blur="validateTaxIdLive"
                />
                <small v-if="fieldErrors.customer_tax_id" class="field-error">{{
                  fieldErrors.customer_tax_id
                }}</small>
                <small v-else-if="taxIdConfig.hint" class="field-hint">{{
                  taxIdConfig.hint
                }}</small>
              </div>

              <div class="field">
                <label>{{ $t('manualInvoiceModal.fields.emailLabel') }} <span class="field-optional">{{ $t('manualInvoiceModal.fields.optional') }}</span></label>
                <input
                  v-model.trim="form.customer_email"
                  type="email"
                  class="field-input"
                />
                <small v-if="fieldErrors.customer_email" class="field-error">{{
                  fieldErrors.customer_email
                }}</small>
              </div>

              <div class="field field--full">
                <label>{{ $t('manualInvoiceModal.fields.phoneLabel') }}</label>
                <input
                  v-model.trim="form.customer_phone"
                  type="tel"
                  class="field-input"
                />
              </div>

              <div class="field field--full">
                <label>{{ $t('manualInvoiceModal.fields.addressLabel') }} <span class="field-optional">{{ $t('manualInvoiceModal.fields.optional') }}</span></label>
                <input
                  v-model.trim="form.customer_address"
                  type="text"
                  class="field-input"
                />
                <small
                  v-if="fieldErrors.customer_address"
                  class="field-error"
                  >{{ fieldErrors.customer_address }}</small
                >
              </div>

              <div class="field">
                <label>{{ addressPlaceholders.cityLabel }} <span class="field-optional">{{ $t('manualInvoiceModal.fields.optional') }}</span></label>
                <input
                  v-model.trim="form.customer_city"
                  type="text"
                  class="field-input"
                />
                <small v-if="fieldErrors.customer_city" class="field-error">{{
                  fieldErrors.customer_city
                }}</small>
              </div>

              <div class="field">
                <label>
                  {{ addressPlaceholders.stateLabel }}
                  <span v-if="requiresProvinceAndZip">*</span>
                  <span v-else class="field-optional">{{ $t('manualInvoiceModal.fields.optional') }}</span>
                </label>
                <input
                  v-model.trim="form.customer_state"
                  type="text"
                  class="field-input"
                />
                <small v-if="fieldErrors.customer_state" class="field-error">{{
                  fieldErrors.customer_state
                }}</small>
              </div>

              <div class="field">
                <label>
                  {{ addressPlaceholders.zipLabel }}
                  <span v-if="requiresProvinceAndZip">*</span>
                  <span v-else class="field-optional">{{ $t('manualInvoiceModal.fields.optional') }}</span>
                </label>
                <input
                  v-model.trim="form.customer_zip"
                  type="text"
                  class="field-input"
                />
                <small v-if="fieldErrors.customer_zip" class="field-error">{{
                  fieldErrors.customer_zip
                }}</small>
              </div>

              <div class="manual-section field--full">
                <span class="manual-section__label">{{ $t('manualInvoiceModal.fields.sectionInvoiceData') }}</span>
              </div>

              <div class="field">
                <label>{{ $t('manualInvoiceModal.fields.paymentSourceLabel') }}</label>
                <div class="pay-src">
                  <button
                    v-for="o in paymentSourceOptions"
                    :key="o.value"
                    type="button"
                    class="pay-src__chip"
                    :class="{ 'is-on': form.payment_source.includes(o.value) }"
                    @click="togglePaymentSource(o.value)"
                  >{{ o.label }}</button>
                </div>
                <small class="field-hint">
                  <template v-if="paymentSourcePrefilled">
                    {{ $t('manualInvoiceModal.fields.paymentHintPrefilled') }}
                  </template>
                  <template v-else-if="form.payment_source.length">
                    {{ $t('manualInvoiceModal.fields.paymentHintMulti') }}
                  </template>
                  <template v-else>
                    {{ $t('manualInvoiceModal.fields.paymentHintNone') }}
                  </template>
                </small>
              </div>

              <div v-if="seriesPrefix" class="field">
                <label>{{ $t('manualInvoiceModal.fields.seriesLabel') }}</label>
                <input
                  :value="seriesPrefix"
                  type="text"
                  class="field-input field-input--readonly"
                  readonly
                />
                <small class="field-hint">
                  {{ $t('manualInvoiceModal.fields.seriesHint') }}
                </small>
              </div>

              <div class="field field--full">
                <label>{{ $t('manualInvoiceModal.fields.notesLabel') }}</label>
                <textarea
                  v-model.trim="form.invoice_notes"
                  class="field-input field-input--textarea"
                  rows="3"
                ></textarea>
                <small class="field-hint"
                  >{{ $t('manualInvoiceModal.fields.notesHint') }}</small
                >
              </div>
            </div>

            <div class="preview-box preview-box--draft">
              <span class="preview-box__label">{{ $t('manualInvoiceModal.preview.label') }}</span>
              <span v-if="estimatedNext" class="preview-box__note">
                <strong class="preview-box__est">{{ estimatedNext }}</strong>
                <span class="preview-box__hint">{{ $t('manualInvoiceModal.preview.estHint') }}</span>
              </span>
              <span v-else class="preview-box__note">{{ $t('manualInvoiceModal.preview.noNumber') }}</span>
            </div>

            </template><!-- fin template formulario -->
          </div>

          <div class="modal-footer">
            <!-- Paso 0: elegir el alcance -->
            <template v-if="scopeStep">
              <button class="btn-cancel" @click="close" :disabled="isProcessing">
                {{ $t('manualInvoiceModal.footer.cancel') }}
              </button>
              <button class="btn-save" @click="scopeStep = false">
                {{ $t('manualInvoiceModal.scope.continue') }}
                <i class="fas fa-arrow-right"></i>
              </button>
            </template>

            <!-- Paso formulario -->
            <template v-else-if="!confirmStep">
              <button class="btn-cancel" @click="close" :disabled="isProcessing">
                {{ $t('manualInvoiceModal.footer.cancel') }}
              </button>
              <button
                class="btn-save"
                @click="goToConfirm"
                :disabled="isProcessing || !reservationId"
              >
                <i class="fas fa-eye" style="color:#fff"></i>
                {{ $t('manualInvoiceModal.footer.create') }}
              </button>
            </template>
            <!-- Paso confirmación -->
            <template v-else>
              <button class="btn-cancel" @click="confirmStep = false" :disabled="isProcessing">
                <i class="fas fa-arrow-left" style="font-size:10px"></i>
                {{ $t('manualInvoiceModal.footer.editData') }}
              </button>
              <button
                class="btn-save"
                @click="submit"
                :disabled="isProcessing || !reservationId"
              >
                <span v-if="isProcessing" class="btn-spinner"></span>
                <i v-else class="fas fa-check" style="color: #fff"></i>
                {{ isProcessing ? $t('manualInvoiceModal.footer.creating') : $t('manualInvoiceModal.footer.createDraft') }}
              </button>
            </template>
          </div>
        </template>
        <!-- ── SUCCESS STATE ───────────────────────────────────────────── -->
        <div v-else class="success-view">
          <div class="success-view__header">
            <div class="success-view__headline">
              <h3 class="success-view__title">{{ $t('manualInvoiceModal.success.title') }}</h3>
              <span
                v-if="createdInvoice"
                class="success-status-badge success-status-badge--sent"
              >
                {{ $t('manualInvoiceModal.success.badge') }}
              </span>
            </div>
            <button class="btn-close" @click="closeSuccess">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="success-view__body">
            <!-- PDF status notice -->
            <div
              v-if="!createdInvoice || !createdInvoice.pdf_url"
              class="success-processing-note"
            >
              <span class="btn-spinner btn-spinner--dark"></span>
              <span
                >{{ $t('manualInvoiceModal.success.pdfGenerating') }}</span
              >
            </div>

            <div class="success-actions">
              <a
                v-if="createdInvoice && createdInvoice.pdf_url"
                :href="createdInvoice.pdf_url"
                target="_blank"
                class="success-btn success-btn--pdf"
              >
                <i class="fas fa-file-pdf" style="color:white"></i>
                <span>{{ $t('manualInvoiceModal.success.downloadPdf') }}</span>
              </a>
              <span
                v-else
                class="success-btn success-btn--pdf success-btn--disabled"
              >
                <i class="fas fa-file-pdf"></i>
                <span>{{ $t('manualInvoiceModal.success.generatingPdf') }}</span>
              </span>

              <button
                class="success-btn success-btn--whatsapp"
                @click="sendWhatsappInvoice"
                :disabled="!createdInvoice || !createdInvoice.pdf_url"
              >
                <i class="fab fa-whatsapp"></i>
                <span>{{ $t('manualInvoiceModal.success.sendWhatsapp') }}</span>
              </button>

              <button
                class="success-btn success-btn--close"
                @click="closeSuccess"
              >
                <i class="fas fa-times-circle"></i>
                <span>{{ $t('manualInvoiceModal.success.closeWindow') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import { $toast } from "@/utils/useToast";
import { buildCountryOptions } from "@/utils/countryOptions";

// Códigos/placeholders fiscales se mantienen literales (nombres propios); solo se
// externalizan las ayudas (hint) y las etiquetas descriptivas (default y matices).
function buildTaxIdConfig(t) {
  return {
    ES: {
      individual: {
        label: "DNI / NIE / NIF",
        placeholder: "12345678A",
        hint: t("manualInvoiceModal.taxId.hints.esIndividual"),
        required: false,
      },
      company: {
        label: t("manualInvoiceModal.taxId.labels.esCompany"),
        placeholder: "B83186860",
        hint: t("manualInvoiceModal.taxId.hints.esCompany"),
        required: true,
      },
    },
    PT: {
      individual: {
        label: "NIF",
        placeholder: "123456789",
        hint: t("manualInvoiceModal.taxId.hints.ptIndividual"),
        required: false,
      },
      company: {
        label: "NIF",
        placeholder: "500123456",
        hint: t("manualInvoiceModal.taxId.hints.ptCompany"),
        required: true,
      },
    },
    CO: {
      individual: {
        label: "Cédula",
        placeholder: "1234567890",
        hint: t("manualInvoiceModal.taxId.hints.coIndividual"),
        required: false,
      },
      company: {
        label: "NIT",
        placeholder: "900123456-7",
        hint: t("manualInvoiceModal.taxId.hints.coCompany"),
        required: true,
      },
    },
    MX: {
      individual: {
        label: "RFC",
        placeholder: "ABCD800101XXX",
        hint: t("manualInvoiceModal.taxId.hints.mxIndividual"),
        required: false,
      },
      company: {
        label: "RFC",
        placeholder: "ABC800101XXX",
        hint: t("manualInvoiceModal.taxId.hints.mxCompany"),
        required: true,
      },
    },
    AR: {
      individual: {
        label: "CUIL / CUIT",
        placeholder: "20-12345678-6",
        hint: t("manualInvoiceModal.taxId.hints.arIndividual"),
        required: false,
      },
      company: {
        label: "CUIT",
        placeholder: "30-12345678-9",
        hint: t("manualInvoiceModal.taxId.hints.arCompany"),
        required: true,
      },
    },
    CL: {
      individual: {
        label: "RUT",
        placeholder: "12345678-9",
        hint: t("manualInvoiceModal.taxId.hints.clIndividual"),
        required: false,
      },
      company: {
        label: "RUT",
        placeholder: "76123456-7",
        hint: t("manualInvoiceModal.taxId.hints.clCompany"),
        required: true,
      },
    },
    PE: {
      individual: {
        label: "DNI",
        placeholder: "12345678",
        hint: t("manualInvoiceModal.taxId.hints.peIndividual"),
        required: false,
      },
      company: {
        label: "RUC",
        placeholder: "20123456789",
        hint: t("manualInvoiceModal.taxId.hints.peCompany"),
        required: true,
      },
    },
    EC: {
      individual: {
        label: "Cédula",
        placeholder: "1234567890",
        hint: t("manualInvoiceModal.taxId.hints.ecIndividual"),
        required: false,
      },
      company: {
        label: "RUC",
        placeholder: "1790012345001",
        hint: t("manualInvoiceModal.taxId.hints.ecCompany"),
        required: true,
      },
    },
    BR: {
      individual: {
        label: t("manualInvoiceModal.taxId.labels.brIndividual"),
        placeholder: "",
        hint: t("manualInvoiceModal.taxId.hints.brIndividual"),
        required: false,
      },
      company: {
        label: "CNPJ",
        placeholder: "12.345.678/0001-90",
        hint: t("manualInvoiceModal.taxId.hints.brCompany"),
        required: true,
      },
    },
    DEFAULT: {
      individual: {
        label: t("manualInvoiceModal.taxId.labels.defaultIndividual"),
        placeholder: "",
        hint: t("manualInvoiceModal.taxId.hints.defaultIndividual"),
        required: false,
      },
      company: {
        label: t("manualInvoiceModal.taxId.labels.defaultCompany"),
        placeholder: "",
        hint: t("manualInvoiceModal.taxId.hints.defaultCompany"),
        required: true,
      },
    },
  };
}

// Solo se externalizan las etiquetas mostradas (cityLabel/stateLabel/zipLabel).
// Los valores de ejemplo (street/city/state/zip) no se renderizan y quedan literales.
function buildAddressConfig(t) {
  return {
    ES: {
      street: "Calle Gran Vía 42",
      cityLabel: t("manualInvoiceModal.address.ES.city"),
      city: "Madrid",
      stateLabel: t("manualInvoiceModal.address.ES.state"),
      state: "Madrid",
      zipLabel: t("manualInvoiceModal.address.ES.zip"),
      zip: "28013",
    },
    PT: {
      street: "Rua Augusta 45",
      cityLabel: t("manualInvoiceModal.address.PT.city"),
      city: "Lisboa",
      stateLabel: t("manualInvoiceModal.address.PT.state"),
      state: "Lisboa",
      zipLabel: t("manualInvoiceModal.address.PT.zip"),
      zip: "1100-048",
    },
    CO: {
      street: "Cra 7 # 32-16",
      cityLabel: t("manualInvoiceModal.address.CO.city"),
      city: "Bogotá",
      stateLabel: t("manualInvoiceModal.address.CO.state"),
      state: "Cundinamarca",
      zipLabel: t("manualInvoiceModal.address.CO.zip"),
      zip: "110111",
    },
    MX: {
      street: "Av. Reforma 222",
      cityLabel: t("manualInvoiceModal.address.MX.city"),
      city: "Ciudad de México",
      stateLabel: t("manualInvoiceModal.address.MX.state"),
      state: "CDMX",
      zipLabel: t("manualInvoiceModal.address.MX.zip"),
      zip: "06600",
    },
    AR: {
      street: "Av. Corrientes 1234",
      cityLabel: t("manualInvoiceModal.address.AR.city"),
      city: "Buenos Aires",
      stateLabel: t("manualInvoiceModal.address.AR.state"),
      state: "Buenos Aires",
      zipLabel: t("manualInvoiceModal.address.AR.zip"),
      zip: "C1043",
    },
    CL: {
      street: "Av. Providencia 1234",
      cityLabel: t("manualInvoiceModal.address.CL.city"),
      city: "Santiago",
      stateLabel: t("manualInvoiceModal.address.CL.state"),
      state: "Metropolitana",
      zipLabel: t("manualInvoiceModal.address.CL.zip"),
      zip: "7500000",
    },
    BR: {
      street: "Rua das Flores 123",
      cityLabel: t("manualInvoiceModal.address.BR.city"),
      city: "São Paulo",
      stateLabel: t("manualInvoiceModal.address.BR.state"),
      state: "SP",
      zipLabel: t("manualInvoiceModal.address.BR.zip"),
      zip: "01310-100",
    },
    US: {
      street: "123 Main Street",
      cityLabel: t("manualInvoiceModal.address.US.city"),
      city: "New York",
      stateLabel: t("manualInvoiceModal.address.US.state"),
      state: "NY",
      zipLabel: t("manualInvoiceModal.address.US.zip"),
      zip: "10001",
    },
    GB: {
      street: "10 Downing Street",
      cityLabel: t("manualInvoiceModal.address.GB.city"),
      city: "London",
      stateLabel: t("manualInvoiceModal.address.GB.state"),
      state: "Greater London",
      zipLabel: t("manualInvoiceModal.address.GB.zip"),
      zip: "SW1A 2AA",
    },
    DEFAULT: {
      street: "Dirección completa",
      cityLabel: t("manualInvoiceModal.address.DEFAULT.city"),
      city: "Ciudad",
      stateLabel: t("manualInvoiceModal.address.DEFAULT.state"),
      state: "Región",
      zipLabel: t("manualInvoiceModal.address.DEFAULT.zip"),
      zip: "Código postal",
    },
  };
}

const EMPTY_FORM = {
  // De qué habitación es la factura. Nulo = toda la reserva (lo de siempre).
  room_instance_id: null,
  // Enlace con el fichero de clientes. Nulos = datos escritos a mano.
  cliente_id: null,
  invoice_recipient_id: null,
  customer_type: "individual",
  customer_name: "",
  customer_tax_id: "",
  customer_country: "",
  customer_email: "",
  customer_phone: "",
  customer_address: "",
  customer_city: "",
  customer_state: "",
  customer_zip: "",
  sequence_number: 1,
  // Lista: una estancia puede cobrarse por varios medios a la vez.
  payment_source: [],
  invoice_notes: "",
};

function onlyDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function validateCIF(cif) {
  const clean = String(cif || "")
    .toUpperCase()
    .replace(/\s/g, "");
  if (!/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[A-J0-9]$/.test(clean)) return false;
  const letters = "JABCDEFGHI";
  let sum = 0;
  for (let i = 1; i <= 7; i += 1) {
    let digit = parseInt(clean[i], 10);
    if (i % 2 === 0) {
      sum += digit;
    } else {
      digit *= 2;
      sum += digit > 9 ? digit - 9 : digit;
    }
  }
  const control = (10 - (sum % 10)) % 10;
  return clean[8] === String(control) || clean[8] === letters[control];
}

function validateNIF(nif) {
  const clean = String(nif || "")
    .toUpperCase()
    .replace(/\s/g, "");
  const letters = "TRWAGMYFPDXBNJZSQVHLCKE";
  const converted = clean.replace(
    /^[XYZ]/,
    (char) => ({ X: "0", Y: "1", Z: "2" }[char]),
  );
  if (!/^\d{8}[A-Z]$/.test(converted)) return false;
  return letters[parseInt(converted.slice(0, 8), 10) % 23] === converted[8];
}

function validatePortugalNIF(nif) {
  const clean = onlyDigits(nif);
  if (!/^\d{9}$/.test(clean) || !/^[1256789]/.test(clean)) return false;
  let sum = 0;
  for (let i = 0; i < 8; i += 1) sum += parseInt(clean[i], 10) * (9 - i);
  const mod = sum % 11;
  const check = mod < 2 ? 0 : 11 - mod;
  return check === parseInt(clean[8], 10);
}

function validateCPF(cpf) {
  const clean = onlyDigits(cpf);
  if (!/^\d{11}$/.test(clean) || /^(\d)\1{10}$/.test(clean)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += parseInt(clean[i], 10) * (10 - i);
  let result = 11 - (sum % 11);
  if (result >= 10) result = 0;
  if (result !== parseInt(clean[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i += 1) sum += parseInt(clean[i], 10) * (11 - i);
  result = 11 - (sum % 11);
  if (result >= 10) result = 0;
  return result === parseInt(clean[10], 10);
}

function validateBrazilRG(value) {
  const clean = String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

  return /^(?:\d{9}|\d{8}[A-Z])$/.test(clean);
}

function validateCNPJ(cnpj) {
  const clean = onlyDigits(cnpj);
  if (!/^\d{14}$/.test(clean) || /^(\d)\1{13}$/.test(clean)) return false;
  const calc = (value, len) => {
    let sum = 0;
    let pos = len - 7;
    for (let i = len; i >= 1; i -= 1) {
      sum += parseInt(value[len - i], 10) * pos;
      pos -= 1;
      if (pos < 2) pos = 9;
    }
    return sum % 11 < 2 ? 0 : 11 - (sum % 11);
  };
  return (
    calc(clean, 12) === parseInt(clean[12], 10) &&
    calc(clean, 13) === parseInt(clean[13], 10)
  );
}

function validateRUTChile(rut) {
  const clean = String(rut || "")
    .replace(/\./g, "")
    .toUpperCase();
  if (!/^\d{7,8}-[\dK]$/.test(clean)) return false;
  const [body, dv] = clean.split("-");
  let sum = 0;
  let factor = 2;
  for (let i = body.length - 1; i >= 0; i -= 1) {
    sum += parseInt(body[i], 10) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }
  const expected = 11 - (sum % 11);
  const control =
    expected === 11 ? "0" : expected === 10 ? "K" : String(expected);
  return dv === control;
}

function validateNITColombia(nit) {
  const clean = onlyDigits(nit);
  if (!/^\d{9,10}$/.test(clean)) return false;
  const base = clean.length === 10 ? clean.slice(0, 9) : clean;
  const weights = [3, 7, 13, 17, 19, 23, 29, 37, 41];
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += parseInt(base[i], 10) * weights[i];
  const remainder = sum % 11;
  const dv = remainder > 1 ? 11 - remainder : remainder;
  return clean.length === 10 ? parseInt(clean[9], 10) === dv : true;
}

function validateCUITArgentina(value) {
  const clean = onlyDigits(value);
  if (!/^\d{11}$/.test(clean)) return false;
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i += 1) sum += parseInt(clean[i], 10) * weights[i];
  const mod = 11 - (sum % 11);
  const check = mod === 11 ? 0 : mod === 10 ? 9 : mod;
  return check === parseInt(clean[10], 10);
}

function validateRUCPeru(ruc) {
  const clean = onlyDigits(ruc);
  if (!/^\d{11}$/.test(clean) || !/^(10|15|16|17|20)/.test(clean)) return false;
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i += 1) sum += parseInt(clean[i], 10) * weights[i];
  const remainder = sum % 11;
  const check = remainder < 2 ? 1 - remainder : 11 - remainder;
  return check === parseInt(clean[10], 10);
}

function validateCedulaEcuador(value) {
  const clean = onlyDigits(value);
  if (!/^\d{10}$/.test(clean)) return false;
  const province = parseInt(clean.slice(0, 2), 10);
  const third = parseInt(clean[2], 10);
  if (province < 1 || province > 24 || third > 5) return false;
  const coeff = [2, 1, 2, 1, 2, 1, 2, 1, 2];
  let sum = 0;
  for (let i = 0; i < 9; i += 1) {
    let current = parseInt(clean[i], 10) * coeff[i];
    if (current >= 10) current -= 9;
    sum += current;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === parseInt(clean[9], 10);
}

function validateRUCEcuador(value) {
  const clean = onlyDigits(value);
  if (!/^\d{13}$/.test(clean) || !clean.endsWith("001")) return false;
  const province = parseInt(clean.slice(0, 2), 10);
  const third = parseInt(clean[2], 10);
  if (province < 1 || province > 24) return false;

  if (third >= 0 && third <= 5) {
    return (
      validateCedulaEcuador(clean.slice(0, 10)) && clean.slice(10) === "001"
    );
  }

  return true;
}

function validateTaxIdByCountry(taxId, countryCode, type, t) {
  if (!taxId || !countryCode) return null;
  const clean = String(taxId || "").trim();
  if (!clean) return null;

  switch (countryCode) {
    case "ES":
      if (type === "company") {
        if (!validateCIF(clean)) return t("manualInvoiceModal.taxErrors.cifCompany");
      } else {
        // Individual: acepta DNI, NIE o pasaporte (cualquier alfanumérico de 6-20 chars)
        if (!validateNIF(clean) && !/^[A-Z0-9]{6,20}$/i.test(clean.replace(/[\s-]/g, ""))) {
          return t("manualInvoiceModal.taxErrors.esIndividual");
        }
      }
      break;
    case "PT":
      if (!validatePortugalNIF(clean))
        return t("manualInvoiceModal.taxErrors.ptNif");
      break;
    case "CO":
      if (type === "company") {
        if (!validateNITColombia(clean)) return t("manualInvoiceModal.taxErrors.nit");
      } else if (!/^\d{6,10}$/.test(onlyDigits(clean))) {
        return t("manualInvoiceModal.taxErrors.coCedula");
      }
      break;
    case "MX":
      if (!/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i.test(clean.replace(/\s/g, "")))
        return t("manualInvoiceModal.taxErrors.rfc");
      break;
    case "AR":
      if (!validateCUITArgentina(clean))
        return type === "company" ? t("manualInvoiceModal.taxErrors.cuit") : t("manualInvoiceModal.taxErrors.cuilCuit");
      break;
    case "CL":
      if (!validateRUTChile(clean)) return t("manualInvoiceModal.taxErrors.rut");
      break;
    case "PE":
      if (type === "company") {
        if (!validateRUCPeru(clean)) return t("manualInvoiceModal.taxErrors.ruc");
      } else if (!/^\d{8}$/.test(onlyDigits(clean))) {
        return t("manualInvoiceModal.taxErrors.peDni");
      }
      break;
    case "EC":
      if (type === "company") {
        if (!validateRUCEcuador(clean)) return t("manualInvoiceModal.taxErrors.ruc");
      } else if (!validateCedulaEcuador(clean)) {
        return t("manualInvoiceModal.taxErrors.ecCedula");
      }
      break;
    case "BR":
      if (type === "company") {
        if (!validateCNPJ(clean)) return t("manualInvoiceModal.taxErrors.cnpj");
      } else if (!validateCPF(clean) && !validateBrazilRG(clean)) {
        return t("manualInvoiceModal.taxErrors.brIndividual");
      }
      break;
    default:
      break;
  }

  return null;
}

export default {
  name: "ManualInvoiceCreateModal",
  components: { Multiselect },
  props: {
    reservationData: { type: Object, default: null },
    billingProfile: { type: Object, default: null },
    openPdfOnCreate: { type: Boolean, default: true },
    /** Cuando se pasa, el modal opera en modo corrección: datos pre-rellenados */
    existingInvoice: { type: Object, default: null },
  },
  data() {
    return {
      showModal: false,
      isProcessing: false,
      isPrefillLoading: false,
      error: "",
      fieldErrors: {},
      form: { ...EMPTY_FORM },
      correlativoFocused: false,
      correlativoTouched: false,
      estimatedNext: null, // estimación informativa del próximo correlativo (al emitir)
      // Las habitaciones de la reserva, para ofrecer «entera o por habitación».
      billableRooms: { splittable: false, currency: 'EUR', rooms: [] },
      selectedCountryOption: null,
      countryOptions: buildCountryOptions("es"),
      prefillSource: null,
      // ── Buscador de clientes del fichero (mismo comportamiento que el de
      // la lista de facturas: GET /guests/search con 220 ms de espera) ──
      selectedClient: null,
      clientSearch: "",
      clients: [],
      clientMenuOpen: false,
      loadingClients: false,
      // Aviso cuando el cliente elegido no tiene perfil fiscal guardado.
      clientFiscalWarning: "",
      // La procedencia se ha deducido de los cobros ya registrados (para avisarlo
      // en pantalla: es un dato que va impreso en la factura, no puede colarse
      // sin que el anfitrión lo vea).
      paymentSourcePrefilled: false,
      // Confirmation step
      confirmStep: false,
      // Paso previo: «¿toda la reserva o una habitación?». Solo con más de una.
      scopeStep: false,
      isCorrectingMode: false,
      // Success state
      successState: false,
      createdInvoice: null,
      pdfPollingTimer: null,
      pdfPollingTicks: 0,
      pdfPollingMaxTicks: 10,
    };
  },
  computed: {
    reservationId() {
      return this.reservationData?.id || null;
    },

    modalTitle() {
      return this.isCorrectingMode
        ? this.$t("manualInvoiceModal.modalTitle.correcting")
        : this.$t("manualInvoiceModal.modalTitle.create");
    },

    /** Dirección completa del cliente para el paso de confirmación */
    confirmSummaryAddress() {
      return [
        this.form.customer_address,
        this.form.customer_city,
        this.form.customer_state,
        this.form.customer_zip,
      ]
        .filter(Boolean)
        .join(", ") || "—";
    },

    /** Colombia usa pasarelas locales: Stripe→Wompi y Bizum→Nequi. */
    isColombia() {
      return String(this.reservationData?.propiedad?.code_pais || "").toUpperCase() === "CO";
    },

    /**
     * Procedencias posibles. Cubren TODOS los métodos con los que se puede
     * registrar un cobro (efectivo, tarjeta, transferencia, pasarela, Bizum/Nequi
     * y OTA), porque si falta alguno ese cobro no se puede trasladar a la factura
     * y se queda en "No especificado" — y el medio de pago sí sale impreso en el PDF.
     */
    paymentSourceOptions() {
      const co = this.isColombia;
      return [
        { value: "efectivo",      label: this.$t("manualInvoiceModal.paymentSource.cash") },
        { value: "tarjeta",       label: this.$t("manualInvoiceModal.paymentSource.card") },
        { value: "stripe",        label: co ? "Wompi" : "Stripe" },
        { value: "transferencia", label: this.$t("manualInvoiceModal.paymentSource.transfer") },
        { value: "bizum",         label: co ? "Nequi" : "Bizum" },
        { value: "booking",       label: "Booking.com" },
        { value: "airbnb",        label: "Airbnb" },
        { value: "ota",           label: this.$t("manualInvoiceModal.paymentSource.otherOta") },
      ];
    },

    /** Etiqueta del pago para el resumen */
    paymentSourceLabel() {
      const nombres = this.form.payment_source
        .map((v) => (this.paymentSourceOptions.find((o) => o.value === v) || {}).label || v);
      return nombres.length ? nombres.join(" + ") : "—";
    },
    seriesPrefix() {
      return this.normalizeSeriesToken(
        this.billingProfile?.invoice_series_prefix || "",
      );
    },

    seriesPadding() {
      return Number(this.billingProfile?.invoice_series_padding || 5);
    },

    previewCode() {
      const number = Number(this.form.sequence_number || 1);
      const padded = String(number).padStart(this.seriesPadding, "0");

      return this.seriesPrefix ? `${this.seriesPrefix}-${padded}` : padded;
    },
    // Correlativo editable: mientras se escribe muestra el número crudo (12);
    // al perder el foco lo muestra rellenado igual que la vista previa (00012).
    correlativoDisplay: {
      get() {
        const n = Number(this.form.sequence_number || 0);
        if (!n) return "";
        return this.correlativoFocused
          ? String(n)
          : String(n).padStart(this.seriesPadding, "0");
      },
      set(val) {
        const digits = String(val).replace(/\D+/g, "");
        this.form.sequence_number = digits ? parseInt(digits, 10) : null;
        // Marca que el usuario TOCÓ el correlativo → loadNextNumber ya no lo pisa.
        this.correlativoTouched = true;
      },
    },
    taxIdConfig() {
      const country = this.form.customer_country || "ES";
      const type =
        this.form.customer_type === "company" ? "company" : "individual";
      const cfg = buildTaxIdConfig(this.$t.bind(this));
      const entry = (cfg[country] || cfg.DEFAULT)[type];
      // El documento es OPCIONAL por ahora (se valida al emitir). Nunca se exige
      // en la creación del borrador — así el anfitrión no se bloquea.
      return { ...entry, required: false };
    },
    addressPlaceholders() {
      const cfg = buildAddressConfig(this.$t.bind(this));
      return cfg[this.form.customer_country] || cfg.DEFAULT;
    },
    requiresProvinceAndZip() {
      return this.form.customer_country === "ES";
    },
    /** Para la etiqueta del cliente elegido (empresa o particular). */
    selectedClientIsCompany() {
      return (
        (this.selectedClient?.billing_profile?.type || "") === "company"
      );
    },

    prefillSourceLabel() {
      const labels = {
        client_file: this.$t("manualInvoiceModal.prefill.clientFile"),
        invoice_recipient: this.$t("manualInvoiceModal.prefill.invoiceRecipient"),
        chat: this.$t("manualInvoiceModal.prefill.chat"),
        checkin: this.$t("manualInvoiceModal.prefill.checkin"),
        reservation: this.$t("manualInvoiceModal.prefill.reservation"),
        existing_invoice: this.$t("manualInvoiceModal.prefill.existingInvoice"),
      };

      return labels[this.prefillSource] || "";
    },
  },
  methods: {
    normalizeSeriesToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/-+$/g, "")
    .replace(/[^A-Z0-9]/g, "");
},

    // Base mínima si no hay check-in o datos fiscales previos.
    buildBaseForm() {
      return {
        /* 🚨 Tiene que estar AQUÍ aunque valga null. En Vue 2, una propiedad que no
           existe cuando se crea el objeto NO es reactiva: al marcarla, el dato
           cambiaba por dentro pero la pantalla no se enteraba, y la tarjeta se
           quedaba sin marcar. Ponerlo solo en EMPTY_FORM no servía de nada, porque
           `open()` arma el formulario con esta función. */
        room_instance_id: null,
        /* Enlace con la ficha del cliente elegido en el buscador (cliente_id) y con
           su perfil fiscal (invoice_recipient_id). Misma trampa de Vue 2 que arriba:
           declarados AQUÍ aunque valgan null, o al elegir un cliente el enlace se
           guardaría por dentro y la pantalla no se enteraría. Nulos = datos a mano. */
        cliente_id: null,
        invoice_recipient_id: null,
        customer_type: "individual",
        // Solo nombre y email se autocompletan desde la reserva. El resto de
        // datos personales/fiscales se completan manualmente (el teléfono y los
        // datos fiscales se gestionan en otro lugar, no se precargan aquí).
        customer_name: this.reservationData?.nombre || "",
        customer_tax_id: "",
        // El país NO se autocompleta: el operador DEBE seleccionarlo según el
        // huésped. Heredarlo (del check-in o del perfil de facturación) provocaba
        // facturas con país equivocado (p.ej. BR para un huésped francés), que
        // Invopop rechazaba al validar el documento como CPF/CNPJ brasileño.
        customer_country: "",
        customer_email: this.reservationData?.email || "",
        customer_phone: "",
        customer_address: "",
        customer_city: "",
        customer_state: "",
        customer_zip: "",
        sequence_number: Number(
          this.billingProfile?.invoice_series_next_number || 1,
        ),
        payment_source: [],
        invoice_notes: "",
      };
    },

    setCustomerType(type) {
      this.form.customer_type = type;
      this.fieldErrors.customer_tax_id = null;
      this.form.customer_tax_id = "";
    },

    syncSelectedCountry(code) {
      this.selectedCountryOption =
        this.countryOptions.find(
          (option) => option.code === String(code || "").toUpperCase(),
        ) || null;
    },

    onCountrySelected(option) {
      const nextCode = option?.code || "";
      if (this.form.customer_country !== nextCode) {
        this.form.customer_country = nextCode;
        this.form.customer_tax_id = "";
        this.fieldErrors.customer_tax_id = null;
      }
    },

    validateTaxIdLive() {
      this.fieldErrors.customer_tax_id =
        validateTaxIdByCountry(
          this.form.customer_tax_id,
          this.form.customer_country,
          this.form.customer_type,
          this.$t.bind(this),
        ) || null;
    },

    normalizeTaxIdInput() {
      this.form.customer_tax_id = String(
        this.form.customer_tax_id || "",
      ).toUpperCase();
    },

    mergePrefill(prefill) {
      const normalizedTaxId = this.normalizePrefilledTaxId(
        prefill.customer_tax_id,
        prefill.customer_country,
        prefill.customer_type,
      );

      this.form = {
        ...this.form,
        customer_type: prefill.customer_type || this.form.customer_type,
        customer_name: prefill.customer_name || this.form.customer_name,
        customer_tax_id: normalizedTaxId || this.form.customer_tax_id,
        customer_country:
          prefill.customer_country || this.form.customer_country,
        customer_email: prefill.customer_email || this.form.customer_email,
        customer_phone: prefill.customer_phone || this.form.customer_phone,
        customer_address:
          prefill.customer_address || this.form.customer_address,
        customer_city: prefill.customer_city || this.form.customer_city,
        customer_state: prefill.customer_state || this.form.customer_state,
        customer_zip: prefill.customer_zip || this.form.customer_zip,
      };

      this.prefillSource = prefill.source || "reservation";
      this.syncSelectedCountry(this.form.customer_country);
    },

    normalizePrefilledTaxId(value, country, type) {
      const taxId = String(value || "").trim();
      if (!taxId) return "";
      if (String(country || "").toUpperCase() !== "BR")
        return taxId.toUpperCase();

      const digits = taxId.replace(/\D/g, "");
      if (type === "company") return digits.length === 14 ? digits : "";
      if (digits.length === 11) return digits;

      const normalized = taxId.toUpperCase().replace(/\s+/g, "");
      return validateBrazilRG(normalized) ? normalized : "";
    },

    /* ── Buscador de clientes del fichero ──────────────────────────────────
       Copiado del selector que ya funciona en la lista de facturas (mismo
       extremo /guests/search y misma espera de 220 ms), para que el operador no
       tenga que teclear a mano una empresa que ya está guardada. */
    clientInitials(client) {
      const name = this.clientDisplayName(client);
      return (
        name
          .split(/\s+/)
          .slice(0, 2)
          .map((part) => part[0] || "")
          .join("")
          .toUpperCase() || "?"
      );
    },

    clientDisplayName(client) {
      return (
        client?.billing_profile?.legal_name ||
        client?.label ||
        [client?.nombre, client?.apellido].filter(Boolean).join(" ") ||
        this.$t("invoiceList.client.fallbackName")
      );
    },

    clientFiscalSummary(client) {
      const billing = client?.billing_profile;
      if (!billing)
        return client?.email || this.$t("invoiceList.client.fiscalPending");
      return (
        [billing.tax_id, billing.city, billing.country_code]
          .filter(Boolean)
          .join(" · ") ||
        billing.email ||
        this.$t("invoiceList.client.fiscalSaved")
      );
    },

    async loadClients() {
      this.loadingClients = true;
      try {
        const { data } = await axios.get("/guests/search", {
          params: { q: this.clientSearch || "" },
        });
        this.clients = Array.isArray(data) ? data : [];
      } catch (e) {
        this.clients = [];
      } finally {
        this.loadingClients = false;
      }
    },

    queueClientSearch() {
      this.openClientMenu();
      clearTimeout(this._clientSearchTimer);
      this._clientSearchTimer = setTimeout(() => this.loadClients(), 220);
    },

    openClientMenu() {
      if (!this.clientMenuOpen) {
        this.clientMenuOpen = true;
        // Cerrar al pinchar fuera: sin esto la lista se queda abierta encima de
        // los campos fiscales y tapa justo lo que hay que revisar.
        document.addEventListener("mousedown", this.onClientMenuOutside, true);
      }
      if (!this.clients.length && !this.loadingClients) this.loadClients();
    },

    closeClientMenu() {
      this.clientMenuOpen = false;
      document.removeEventListener("mousedown", this.onClientMenuOutside, true);
    },

    onClientMenuOutside(event) {
      const target = event.target;
      const inside =
        target && typeof target.closest === "function"
          ? target.closest(".client-pick")
          : null;
      if (!inside) this.closeClientMenu();
    },

    /**
     * Elige un cliente del fichero: vuelca sus datos fiscales en el formulario y
     * guarda el enlace con su ficha. Los campos siguen editables; corregirlos NO
     * borra el enlace (el backend rellena desde la ficha, pero el operador manda
     * sobre lo que ve).
     */
    selectClient(client) {
      if (!client) return;
      this.closeClientMenu();

      const billing = client.billing_profile || null;

      /* Sin perfil fiscal no hay nada que copiar: se avisa y NO se enlaza la
         ficha, para no emitir con los datos fiscales en blanco creyendo que
         venían del fichero. Se traen nombre y email, que sí sirven. */
      if (!billing) {
        this.clientFiscalWarning = this.$t(
          "manualInvoiceModal.client.noFiscalProfile",
        );
        this.form.cliente_id = null;
        this.form.invoice_recipient_id = null;
        const nombre = [client.nombre, client.apellido]
          .filter(Boolean)
          .join(" ");
        if (nombre) this.form.customer_name = nombre;
        if (client.email) this.form.customer_email = client.email;
        return;
      }

      this.clientFiscalWarning = "";
      this.selectedClient = client;
      this.clientSearch = "";
      this.form.cliente_id = client.id || null;
      this.form.invoice_recipient_id = billing.id || null;

      // Se reutiliza mergePrefill: normaliza el documento según el país y deja lo
      // que la ficha no traiga tal y como estuviera (nada se borra a traición).
      this.mergePrefill({
        customer_type: billing.type === "company" ? "company" : "individual",
        customer_name: billing.legal_name || this.clientDisplayName(client),
        customer_tax_id: billing.tax_id,
        customer_country: String(billing.country_code || "")
          .toUpperCase()
          .slice(0, 2),
        customer_email: billing.email || client.email,
        customer_phone: billing.phone || client.whatsapp,
        customer_address: [billing.address_line1, billing.address_line2]
          .filter(Boolean)
          .join(", "),
        customer_city: billing.city,
        customer_state: billing.state,
        customer_zip: billing.postal_code,
        source: "client_file",
      });
    },

    /**
     * Quita la selección y devuelve el formulario a mano. Los datos ya volcados
     * se QUEDAN (no se tira lo que el operador pueda estar corrigiendo): lo único
     * que se suelta es el enlace con la ficha.
     */
    clearSelectedClient() {
      this.selectedClient = null;
      this.clientSearch = "";
      this.clientFiscalWarning = "";
      this.form.cliente_id = null;
      this.form.invoice_recipient_id = null;
      if (this.prefillSource === "client_file") this.prefillSource = null;
      this.clients = [];
      this.$nextTick(() => this.openClientMenu());
    },

    /** Deja el buscador como recién abierto (al abrir o cerrar el modal). */
    resetClientPicker() {
      clearTimeout(this._clientSearchTimer);
      this.closeClientMenu();
      this.selectedClient = null;
      this.clientSearch = "";
      this.clients = [];
      this.loadingClients = false;
      this.clientFiscalWarning = "";
    },

    async loadPrefill() {
      if (!this.reservationId) return;

      this.isPrefillLoading = true;
      try {
        const { data } = await axios.get(
          `/reservas/${this.reservationId}/invoices/prefill`,
        );
        if (data?.data) {
          const p = data.data;
          if (p.source === "chat") {
            // El HUÉSPED indicó sus datos fiscales al solicitar su factura (motor/chat)
            // → son los del destinatario correcto: se precargan TODOS.
            this.mergePrefill(p);
          } else {
            // Del CHECK-IN solo nombre+email: el resto arrastraba datos del responsable
            // de la reserva, que no siempre es el destinatario fiscal.
            if (p.customer_name) this.form.customer_name = p.customer_name;
            if (p.customer_email) this.form.customer_email = p.customer_email;
            this.prefillSource = p.source || "checkin";
          }
        }
      } catch (error) {
        this.prefillSource = "reservation";
      } finally {
        this.isPrefillLoading = false;
      }
    },

    /**
     * Abre el modal. Si se pasa `invoiceData`, entra en modo corrección
     * y pre-rellena el formulario con los datos de esa factura.
     */
    /** Marca o desmarca un medio de cobro. */
    togglePaymentSource(valor) {
      const i = this.form.payment_source.indexOf(valor);
      if (i >= 0) this.form.payment_source.splice(i, 1);
      else this.form.payment_source.push(valor);
      // A partir de aquí manda el anfitrión: se retira el aviso de "lo pusimos nosotros".
      this.paymentSourcePrefilled = false;
    },

    /**
     * Marca los medios con los que YA se cobró la reserva, para no preguntar dos
     * veces lo mismo. Se marcan TODOS los empleados: si la estancia se pagó parte
     * con tarjeta y parte por transferencia, salen los dos, que es justo lo que
     * luego se desglosa en la factura.
     *
     * Solo cuentan los cobros hechos: un enlace de pago pendiente o fallido
     * todavía no dice por dónde entró el dinero.
     */
    async prefillPaymentSource() {
      this.paymentSourcePrefilled = false;
      const id = this.reservationData?.id;
      if (!id) return;

      try {
        const { data } = await axios.get(`/reservas/${id}/payments`);
        const lines = (data?.payments || []).filter((p) => p && p.method && p.status === "paid");
        if (!lines.length) return;

        const ota = String(this.reservationData?.ota_name || "").toLowerCase();
        const mapa = {
          cash:     "efectivo",
          card:     "tarjeta",
          transfer: "transferencia",
          online:   "stripe",
          bizum:    "bizum",
          nequi:    "bizum",
          ota:      ota.includes("booking") ? "booking" : (ota.includes("airbnb") ? "airbnb" : "ota"),
        };

        // Sin duplicar y respetando el orden en que se cobró.
        const medios = [];
        lines.forEach((p) => {
          const m = mapa[p.method];
          if (m && !medios.includes(m)) medios.push(m);
        });
        if (!medios.length) return;

        this.form.payment_source = medios;
        this.paymentSourcePrefilled = true;
      } catch (e) {
        // Sin cobros consultables se queda vacío: el campo sigue siendo editable.
      }
    },

    /** Pasa a «una habitación» quedándose con la primera si no había ninguna. */
    elegirPorHabitacion() {
      if (this.form.room_instance_id) return;
      const primera = this.billableRooms.rooms[0];
      this.form.room_instance_id = primera ? primera.id : null;
    },

    /** El importe de una habitación, con el formato de siempre. */
    fmtRoom(n) {
      return parseFloat(n || 0).toLocaleString('es-ES', { useGrouping: "always", minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    async open(invoiceData = null) {
      this.error = "";
      this.fieldErrors = {};
      this.isProcessing = false;
      this.confirmStep = false;
      this.successState = false;
      this.createdInvoice = null;
      this.form = this.buildBaseForm();
      this.resetClientPicker();
      this.correlativoTouched = false;
      this.prefillSource = "reservation";
      this.syncSelectedCountry(this.form.customer_country);

      /* Qué habitaciones hay. Se ESPERA a la respuesta: si la reserva tiene varias,
         lo primero que se ve es la pregunta de si la factura es entera o de una, y no
         el formulario. Si falla, se sigue como siempre —toda la reserva—: antes no
         ofrecer la opción que ofrecerla y que al guardar reviente. */
      this.billableRooms = { splittable: false, currency: 'EUR', rooms: [] };
      this.scopeStep = false;

      try {
        const { data } = await axios.get(`/reservas/${this.reservationId}/billable-rooms`);
        this.billableRooms = {
          splittable: !!data.splittable,
          currency: data.currency || 'EUR',
          rooms: data.rooms || [],
        };
        this.scopeStep = this.billableRooms.splittable;
      } catch (e) {
        this.billableRooms = { splittable: false, currency: 'EUR', rooms: [] };
      }

      // Modo corrección: pre-rellenar con datos de la factura existente
      const src = invoiceData || this.existingInvoice || null;
      if (src) {
        this.isCorrectingMode = true;
        this.mergePrefill({
          customer_type:    src.customer_type    || "individual",
          customer_name:    src.customer_name,
          customer_tax_id:  src.customer_tax_id,
          customer_country: src.customer_country,
          customer_email:   src.customer_email,
          customer_phone:   src.customer_phone,
          customer_address: src.customer_address,
          customer_city:    src.customer_city,
          customer_state:   src.customer_state,
          customer_zip:     src.customer_zip,
          source: "existing_invoice",
        });
      } else {
        this.isCorrectingMode = false;
        // Si el anfitrión ya dijo de dónde venía el dinero al registrar el cobro,
        // no tiene por qué repetirlo aquí.
        this.prefillPaymentSource();
      }

      this.showModal = true;

      // Teletransporta el overlay a <body> para que escape del stacking context del
      // drawer de la reserva (si no, los iconos del timeline quedan por encima). El
      // modal usa colores SCSS compilados, así que no pierde estilos al moverse.
      this.$nextTick(() => {
        const el = this.$refs.createOverlay;
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el);
          this._createOverlayEl = el;
        }
      });

      // Solo carga prefill del servidor si NO estamos en modo corrección.
      // NOTA: el borrador NO lleva número (se asigna al emitir). loadNextNumber solo
      // trae una ESTIMACIÓN informativa del próximo correlativo para mostrarla.
      if (!src) {
        await this.loadPrefill();
        await this.loadNextNumber();
      }
    },

    // ESTIMACIÓN informativa del próximo correlativo (el que TENDRÍA al emitir).
    // NO se guarda ni se envía: el número real nace al emitir y el primero que emita
    // se lo lleva. Solo se muestra para que el operador sepa por dónde va la serie.
    async loadNextNumber() {
      try {
        const params = {};
        if (this.billingProfile?.id) params.billing_profile_id = this.billingProfile.id;
        const { data } = await axios.get("/business/invoices/next-number", { params });
        if (data && data.display) {
          this.estimatedNext = data.display;
        }
      } catch (e) {
        /* sin conexión → no se muestra estimación */
      }
    },

    close() {
      if (this.isProcessing) return;
      this.stopPdfPolling();
      this.closeClientMenu();
      this.showModal = false;
      this.successState = false;
      this.confirmStep = false;
      this.createdInvoice = null;
    },

    closeSuccess() {
      this.stopPdfPolling();
      this.showModal = false;
      this.successState = false;
      this.confirmStep = false;
      this.createdInvoice = null;
    },

    normalizeInvoice(invoice) {
      if (!invoice) return null;

      const customer = invoice.customer ?? {};
      const billing = invoice.billing ?? {};
      const invopop = invoice.invopop ?? {};

      return {
        ...invoice,
        status:
          invopop.status ??
          invoice.invopop_status ??
          invoice.status ??
          "pending",
        customer_name: customer.name ?? invoice.customer_name,
        customer_tax_id: customer.tax_id ?? invoice.customer_tax_id,
        customer_email: customer.email ?? invoice.customer_email,
        customer_country: customer.country ?? invoice.customer_country,
        customer_address: customer.address ?? invoice.customer_address,
        customer_city: customer.city ?? invoice.customer_city,
        customer_state: customer.state ?? invoice.customer_state,
        customer_zip: customer.zip ?? invoice.customer_zip,
        currency: billing.currency ?? invoice.currency,
        total: billing.total ?? invoice.total,
        series: billing.series ?? invoice.series ?? "",
        sequence_number: billing.sequence_number ?? invoice.sequence_number ?? null,
        display_code: billing.display_code ?? invoice.display_code ?? null,
        pdf_url: invopop.pdf_url ?? invoice.invopop_pdf_url ?? invoice.pdf_url ?? null,
        error: invopop.error ?? invoice.invopop_error ?? invoice.error ?? null,
        has_pdf: invopop.has_pdf ?? Boolean(invoice.invopop_pdf_url || invoice.pdf_url),
        payment_method_label:
          invoice.payment_method_label ?? invoice.payment_method ?? null,
      };
    },

    shouldKeepPolling(invoice) {
      return Boolean(
        invoice &&
          (["pending", "processing"].includes(invoice.status) ||
            (invoice.status === "sent" && !invoice.pdf_url)) &&
          !invoice.pdf_url,
      );
    },

    stopPdfPolling() {
      if (!this.pdfPollingTimer) return;
      window.clearInterval(this.pdfPollingTimer);
      this.pdfPollingTimer = null;
      this.pdfPollingTicks = 0;
    },

    startPdfPolling(invoiceId) {
      if (!invoiceId || this.pdfPollingTimer) return;

      this.pdfPollingTicks = 0;
      this.pdfPollingTimer = window.setInterval(async () => {
        this.pdfPollingTicks += 1;

        try {
          const { data } = await axios.post(`/business/invoices/${invoiceId}/sync`);
          const normalized = this.normalizeInvoice(data);
          this.createdInvoice = normalized;
          this.$emit("created", normalized);

          if (!this.shouldKeepPolling(normalized) || this.pdfPollingTicks >= this.pdfPollingMaxTicks) {
            this.stopPdfPolling();
          }
        } catch (e) {
          this.stopPdfPolling();
        }
      }, 2000);
    },

    formatAmount(value) {
      const num = parseFloat(value);
      if (isNaN(num)) return "—";
      return num.toLocaleString("es-ES", { useGrouping: "always",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    statusLabel(status) {
      const labels = {
        sent: this.$t("manualInvoiceModal.status.sent"),
        processing: this.$t("manualInvoiceModal.status.processing"),
        pending: this.$t("manualInvoiceModal.status.pending"),
        error: this.$t("manualInvoiceModal.status.error"),
      };
      return labels[status] || status || "—";
    },

    async sendWhatsappInvoice() {
      const invoiceId = this.createdInvoice?.id;
      if (!invoiceId || !this.createdInvoice?.pdf_url) return;

      try {
        const { data } = await axios.post(
          `/business/invoices/${invoiceId}/send-whatsapp`,
        );
        $toast.success(data?.message || this.$t("manualInvoiceModal.toast.whatsappSent"));
      } catch (error) {
        $toast.error(
          error?.response?.data?.message ||
            this.$t("manualInvoiceModal.toast.whatsappError"),
        );
      }
    },

    /** Valida el formulario y avanza al paso de confirmación */
    goToConfirm() {
      /* 🚨 Si algo no valida, DECIRLO. Antes se volvía sin más: el error se quedaba
         pegado a su campo, muchas veces fuera de la parte visible del modal, y desde
         fuera parecía que el botón no hacía nada. Ahora sale arriba, en el aviso que
         ya existe, y el modal se sube para que se lea. */
      if (!this.validate()) {
        const primero = Object.values(this.fieldErrors || {})[0];
        this.error = primero || this.$t('manualInvoiceModal.errors.checkData');
        this.$nextTick(() => {
          const caja = this.$el?.querySelector('.modal-body, .manual-modal__body');
          if (caja) caja.scrollTop = 0;
        });

        return;
      }

      if (!this.reservationId) {
        this.error = this.$t('manualInvoiceModal.errors.createFailed');

        return;
      }

      this.error = '';
      this.confirmStep = true;
    },

    validate() {
      const errors = {};

      if (!this.form.customer_name) {
        errors.customer_name = this.$t("manualInvoiceModal.validation.nameRequired");
      }

      // País OPCIONAL: solo se valida el formato si se rellena (por defecto se toma
      // el país del emisor al construir la factura).
      if (
        this.form.customer_country &&
        String(this.form.customer_country).trim().length !== 2
      ) {
        errors.customer_country = this.$t("manualInvoiceModal.validation.countryInvalid");
      }

      // Email: solo validar formato si se proporciona (no es obligatorio)
      if (this.form.customer_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.customer_email)) {
        errors.customer_email = this.$t("manualInvoiceModal.validation.emailInvalid");
      }

      // Dirección, ciudad, provincia y CP: todos opcionales
      // Invopop/GOBL puede advertir si faltan, pero no bloqueamos aquí

      if (this.taxIdConfig.required && !this.form.customer_tax_id) {
        errors.customer_tax_id = this.$t(
          "manualInvoiceModal.validation.taxIdRequired",
        );
      } else if (this.form.customer_tax_id) {
        const taxError = validateTaxIdByCountry(
          this.form.customer_tax_id,
          this.form.customer_country,
          this.form.customer_type,
          this.$t.bind(this),
        );
        if (taxError) errors.customer_tax_id = taxError;
      }

      // (El borrador no lleva correlativo: se asigna al emitir. No se valida aquí.)

      this.fieldErrors = errors;
      this.error = "";
      return Object.keys(errors).length === 0;
    },

    // Crea la factura. Llamado desde el paso de confirmación (datos ya validados).
    async submit() {
      if (!this.reservationId) return;

      this.isProcessing = true;
      this.stopPdfPolling();

      try {
        const payload = {
          customer_type: this.form.customer_type,
          customer_name: this.form.customer_name,
          customer_tax_id: this.form.customer_tax_id || null,
          customer_country: String(
            this.form.customer_country || "ES",
          ).toUpperCase(),
          customer_email: this.form.customer_email || null,
          customer_phone: this.form.customer_phone || null,
          customer_address: this.form.customer_address || null,
          customer_city: this.form.customer_city || null,
          customer_state: this.form.customer_state || null,
          customer_zip: this.form.customer_zip || null,
          // El backend guarda los medios separados por comas.
          payment_method: this.form.payment_source.length
            ? this.form.payment_source.join(",")
            : null,
          notes: this.form.invoice_notes?.trim() || null,
          // Nulo = toda la reserva (la factura de siempre).
          room_instance_id: this.form.room_instance_id || null,
          /* Enlace con el fichero de clientes cuando se eligió uno en el buscador:
             el backend toma los datos de su ficha y la factura queda atada a ese
             cliente. Nulos = datos escritos a mano, como hasta ahora. */
          cliente_id: this.form.cliente_id || null,
          invoice_recipient_id: this.form.invoice_recipient_id || null,
        };

        const { data } = await axios.post(
          `/reservas/${this.reservationId}/invoices/draft`,
          payload,
        );

        const invoice = this.normalizeInvoice(data?.data || data || null);
        $toast.success(this.$t("manualInvoiceModal.toast.draftCreated"));
        this.$emit("created", invoice);

        // Es un BORRADOR: sin número ni PDF todavía. NO mostramos la pantalla de
        // "factura emitida"; cerramos el modal y la tarjeta queda en estado Borrador.
        this.showModal = false;
        this.confirmStep = false;
      } catch (e) {
        const status = e?.response?.status;
        const body = e?.response?.data || {};

        if (status === 409) {
          this.error =
            body?.message || this.$t("manualInvoiceModal.errors.conflict");
          $toast.warning(this.error);
        } else if (status === 422) {
          const firstError = Object.values(body?.errors || {}).flat()?.[0];
          this.error =
            firstError || body?.message || this.$t("manualInvoiceModal.errors.checkData");
        } else {
          this.error = body?.message || this.$t("manualInvoiceModal.errors.createFailed");
          $toast.error(this.error);
        }
      } finally {
        this.isProcessing = false;
      }
    },
  },

  beforeDestroy() {
    this.stopPdfPolling();
    clearTimeout(this._clientSearchTimer);
    document.removeEventListener("mousedown", this.onClientMenuOutside, true);
    // Si el overlay quedó teletransportado a <body>, retíralo al destruir el modal.
    if (this._createOverlayEl && this._createOverlayEl.parentNode) {
      this._createOverlayEl.parentNode.removeChild(this._createOverlayEl);
    }
  },
};
</script>
<style scoped lang="scss">
// ─── Variables ───────────────────────────────────────────────────────────────
$brand:   #5e72e4;
$purple:  #673de6;
$green:   #11b14d;
$ink:     #0f172a;
$ink-2:   #334155;
$ink-3:   #64748b;
$ink-4:   #94a3b8;
$border:  #eceef4;
$bg:      #f8fafc;
$radius:  10px;
$font:    "DM Sans", "Inter", system-ui, sans-serif;

// El global de App.scss `body,a,p,h4-6,small,label { Inter !important }` pisa la
// fuente; dentro del modal forzamos DM Sans en esos elementos (labels, ayudas, etc.).
.modal-container label,
.modal-container small,
.modal-container p,
.modal-container a,
.modal-container h4,
.modal-container h5,
.modal-container h6 {
  font-family: $font !important;
}

// ─── Overlay ─────────────────────────────────────────────────────────────────
.modal-overlay {
  position: fixed;
  inset: 0;
  // Por encima del nav/topbar (z-index 99999): un modal debe ir sobre todo, también
  // en la vista de página completa de la reserva (antes quedaba por debajo del nav).
  z-index: 100050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(6px);
  font-family: $font;
}

// ─── Container ───────────────────────────────────────────────────────────────
.modal-container {
  width: 100%;
  max-width: 700px;
  margin: auto 0;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 16px;
  border: 1px solid $border;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.22);
  font-family: $font; /* base DM Sans para todo el modal (spans incluidos) */
}

.modal-container--lg { max-width: 760px; }
.modal-container--success { max-width: 460px; }

// ─── Header ──────────────────────────────────────────────────────────────────
.modal-header {
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid $border;
  background: #fff;
  flex-shrink: 0;

  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 16.5px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: $ink;
    font-family: $font;

    i {
      width: 38px;
      height: 38px;
      border-radius: 11px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: $purple;
      color: #fff;
      font-size: 15px;
    }
  }
}

// ─── Body / Footer ───────────────────────────────────────────────────────────
.modal-body {
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 14px 22px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid $border;
  background: $bg;
  flex-shrink: 0;
}

// ─── Buttons ─────────────────────────────────────────────────────────────────
.btn-close,
.btn-cancel,
.btn-save {
  border: none;
  cursor: pointer;
  font-family: $font;
  transition: background 0.12s, box-shadow 0.12s, opacity 0.12s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.btn-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: $bg;
  color: $ink-2;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: $border; }
}

.btn-cancel {
  padding: 10px 18px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid $border;
  color: $ink-2;
  font-size: 13px;
  font-weight: 600;
  &:hover:not(:disabled) { background: $bg; border-color: #cbd5e1; }
}

.btn-save {
  padding: 10px 22px;
  border-radius: 10px;
  background: $purple;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.12s;
  &:hover:not(:disabled) { background: #5a33cf; }
}

// ─── Info / Alert boxes ──────────────────────────────────────────────────────
.info-box {
  display: flex;
  gap: 12px;
  padding: 13px 15px;
  border-radius: 12px;
  /* Morado de marca en claro, sin borde. */
  background: #f1eefe;
  border: none;
  color: $ink-2;
  font-size: 13px;

  i {
    color: $brand;
    margin-top: 1px;
    flex-shrink: 0;
    font-size: 15px;
  }

  strong { display: block; font-weight: 700; color: $ink; font-size: 13px; margin-bottom: 3px; }
  p { margin: 0; font-size: 12px; color: #475569; line-height: 1.5; }
  small { display: block; margin-top: 6px; font-size: 11.5px; color: $brand; font-weight: 600; }

  &--warn {
    background: #fffbeb;
    border-color: rgba(234, 179, 8, 0.3);
    i { color: #d97706; }
    small { color: #b45309; }
  }
}

.alert-error {
  display: flex;
  gap: 10px;
  padding: 10px 13px;
  border-radius: 9px;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #9f1239;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 12px;
  i { margin-top: 1px; flex-shrink: 0; }
}

.prefill-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 9px 12px;
  border-radius: 8px;
  background: $bg;
  border: 1px solid $border;
  color: $ink-3;
  font-size: 13px;
}

// ─── Type selector ───────────────────────────────────────────────────────────
/* "Tipo de destinatario" también en fila: label izquierda + cards derecha. */
.type-box {
  display: grid;
  grid-template-columns: 190px 1fr;
  align-items: center;
  column-gap: 18px;
  margin-top: 14px;
}

.type-box__label {
  grid-column: 1;
  font-size: 11px;
  font-weight: 700;
  color: $ink-2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-family: $font;
}

.type-grid {
  grid-column: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.type-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 11px;
  border: 1px solid $border;
  border-radius: 10px;
  background: #f9fafc;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
  text-align: left;

  &:hover { background: #f4f6fa; border-color: #dfe3ec; }

  &.active {
    border-color: $brand;
    background: #f1f0fe;
    /* Sombra suave de marca hacia abajo (no el anillo duro). */
    box-shadow: 0 10px 26px -16px rgba(94, 114, 228, 0.55);
  }
}

.type-option__icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: $bg;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.12s, box-shadow 0.12s;

  i { color: $ink-3; font-size: 13px; }

  .active & {
    background: $brand;
    i { color: #fff; }
  }
}

.type-option__text { flex: 1; min-width: 0; }

.type-option__name {
  display: block;
  font-size: 12.5px;
  font-weight: 700;
  color: $ink;
  font-family: $font;

  .active & { color: $brand; }
}

.type-option__sub {
  display: block;
  font-size: 10.5px;
  color: $ink-3;
  font-weight: 400;
  margin-top: 1px;

  .active & { color: rgba($brand, 0.65); }
}

.type-option__badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 3px 9px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(94, 114, 228, 0.35);
  color: $brand;
  font-size: 10px;
  font-weight: 700;
  font-family: $font;

  i { font-size: 8px; }
}

// ─── Cliente del fichero ─────────────────────────────────────────────────────
/* Misma rejilla que «tipo de destinatario» (label a la izquierda, contenido a la
   derecha) para que el buscador no rompa la columna del formulario. */
.client-pick {
  display: grid;
  grid-template-columns: 190px 1fr;
  align-items: start;
  column-gap: 18px;
  row-gap: 5px;
  margin-top: 14px;
}

.client-pick__label {
  grid-column: 1;
  padding-top: 11px;
  font-family: $font;
  font-size: 11px;
  font-weight: 700;
  color: $ink-2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.35;
}

.client-pick__body {
  grid-column: 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.client-pick__search {
  position: relative;
  display: flex;
  align-items: center;
}

/* La lupa acababa en ~25px y el texto arrancaba en 34: 9px de hueco, la mitad
   que en el resto del panel. Se veía el icono pegado a la palabra. */
.client-pick__search-icon {
  position: absolute;
  left: 14px;
  font-size: 12.5px;
  color: $ink-4;
  pointer-events: none;
}

/* 🚨 Anidado a propósito, no por capricho. `.field-input` (padding 9px 13px) y
   `.client-pick__input` tienen la MISMA especificidad, y `.field-input` está más
   abajo en la hoja, así que ganaba ella: el hueco real era de −14 px y la lupa se
   montaba encima de la «S» de «Search». Metiéndolo dentro del contenedor sube la
   especificidad y manda este. */
.client-pick__search .client-pick__input {
  padding-left: 40px;
  padding-right: 38px;
}

.client-pick__spinner { position: absolute; right: 13px; }

.client-pick__menu {
  position: absolute;
  z-index: 5;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  max-height: 232px;
  overflow-y: auto;
  padding: 5px;
  border: 1px solid $border;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.16);
}

.client-pick__option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 9px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s;

  &:hover { background: $bg; }
  &:focus-visible {
    outline: none;
    background: $bg;
    box-shadow: 0 0 0 3.5px rgba(94, 114, 228, 0.12);
  }
}

.client-pick__empty {
  margin: 0;
  padding: 14px 10px;
  color: $ink-3;
  font-size: 12.5px;
  text-align: center;
}

.client-pick__card {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 12px;
  border: 1px solid rgba($brand, 0.28);
  border-radius: 10px;
  background: rgba(94, 114, 228, 0.06);
}

.client-pick__avatar {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  background: #fff;
  color: $brand;
  font-size: 11.5px;
  font-weight: 800;

  &--sm {
    width: 30px;
    height: 30px;
    background: #f1eefe;
    color: $purple;
  }
}

.client-pick__identity {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  strong {
    overflow: hidden;
    color: $ink;
    font-size: 13px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    overflow: hidden;
    color: $ink-3;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.client-pick__type {
  flex: 0 0 auto;
  padding: 4px 9px;
  border-radius: 999px;
  background: #fff;
  color: $brand;
  font-size: 10px;
  font-weight: 700;
}

.client-pick__badge {
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 999px;
  background: #fffbeb;
  color: #b45309;
  font-size: 9.5px;
  font-weight: 700;
}

.client-pick__clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid $border;
  border-radius: 9px;
  background: #fff;
  color: $ink-3;
  font-family: $font;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;

  &:hover:not(:disabled) { color: $ink-2; border-color: #cbd5e1; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  i { font-size: 10px; }
}

/* Cliente sin perfil fiscal: mismo amarillo de aviso que `.info-box--warn`. */
.client-pick__warn {
  display: flex;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 9px;
  background: #fffbeb;
  color: #92400e;
  font-size: 12px;
  line-height: 1.45;

  i { margin-top: 2px; flex-shrink: 0; color: #d97706; }
}

@media (prefers-reduced-motion: reduce) {
  .client-pick__option,
  .client-pick__clear { transition: none; }
}

[dir="rtl"] {
  .client-pick__search-icon { left: auto; right: 14px; }
  .client-pick__spinner { right: auto; left: 13px; }
  .client-pick__option { text-align: right; }
}

// ─── Form grid ───────────────────────────────────────────────────────────────
.manual-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 14px;
}

/* Layout en FILA: label a la izquierda (columna fija) + input a la derecha,
   y la ayuda/el error debajo del input (alineados a su columna). */
.field {
  display: grid;
  grid-template-columns: 190px 1fr;
  align-items: start;
  column-gap: 18px;
  row-gap: 5px;
}

.field--full { grid-column: 1 / -1; }

.field > .field-input,
.field > .multiselect-wrap { grid-column: 2; grid-row: 1; }
.field > .field-hint,
.field > .field-error { grid-column: 2; }

/* Separador sutil entre grupos (línea fina + micro-título). */
.manual-section {
  margin-top: 4px;
  padding-top: 13px;
  border-top: 1px solid $border;
}
/* ── Entera o por habitación ──────────────────────────────────────────────
   Las dos tarjetas usan `.type-option`, el mismo componente que «tipo de
   destinatario», así que de su aspecto y de su estado activo no me ocupo aquí.
   Esto es solo la columna que las envuelve y la lista de habitaciones. */
/* ── El paso de «¿entera o por habitación?» ───────────────────────────────
   Una pregunta con dos respuestas no necesita 700 px: el modal se estrecha y se
   recoge, que así se lee de un vistazo en vez de quedar estirado y vacío. */
.modal-container--ask { max-width: 460px; }
.modal-container--ask .modal-body   { padding: 18px 22px 20px; }
.modal-container--ask .modal-footer { padding: 14px 22px 18px; }

.scope-step { display: flex; flex-direction: column; gap: 14px; }
.scope-step__lead {
  font-family: $font;
  font-size: 14px; color: #4a4e57; line-height: 1.5; margin: 0;
}
.type-grid--stack { grid-template-columns: 1fr; gap: 8px; }

/* En esta pantalla las tarjetas respiran un poco más: son LA decisión. */
.modal-container--ask .type-option { padding: 12px 13px; }
.modal-container--ask .type-option__name { font-size: 14.5px; }

.type-box--top { align-items: start; }
.type-box--top .type-box__label { padding-top: 11px; }

/* Un solo hijo en la columna de contenido: `.type-box` es una rejilla
   etiqueta|contenido y un segundo hijo se iba a la columna de la etiqueta. */
.scope-col { display: flex; flex-direction: column; gap: 10px; min-width: 0; }

.scope-rooms { display: flex; flex-direction: column; gap: 6px; }
.scope-rooms__label { font-size: 12px; font-weight: 600; color: #71757e; }
.scope-rooms__hint  { font-size: 12px; color: #8a8f98; line-height: 1.45; }
.scope-rooms__list  { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 6px; }

.scope-room {
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  border: 1px solid #e8e9ec; background: #f9fafc; border-radius: 10px;
  padding: 9px 12px; cursor: pointer; text-align: left;
  transition: border-color 0.12s, background 0.12s;
}
.scope-room:hover  { background: #fff; }
.scope-room.active { background: #fff; border-color: #673de6; box-shadow: 0 0 0 1px #673de6 inset; }
.scope-room__name   { font-size: 13.5px; font-weight: 600; color: #16181d; }
.scope-room__amount { font-size: 12px; color: #71757e; white-space: nowrap; }

@media (prefers-reduced-motion: reduce) { .scope-room { transition: none; } }

.manual-section__label {
  font-size: 10.5px;
  font-weight: 700;
  color: $ink-4;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: $font;
}

.field > label {
  grid-column: 1;
  grid-row: 1;
  padding-top: 11px; /* alinea el label con el texto del input */
  margin-bottom: 0; /* anula el 0.5rem global de Bootstrap en <label> */
  font-size: 11px;
  font-weight: 700;
  color: $ink-2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.35;
  font-family: $font;
}

.field-input {
  width: 100%;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 9px 13px;
  font-size: 13.5px;
  color: $ink;
  background: #f9fafc;
  font-family: $font;
  box-sizing: border-box;
  height: 40px;
  transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;

  &::placeholder { color: $ink-4; }
  &:hover { background: #f4f6fa; }
  &:focus {
    outline: none;
    background: #fff;
    border-color: $brand;
    box-shadow: 0 0 0 3.5px rgba(94, 114, 228, 0.12);
  }
}

/* Medios de cobro: se marcan varios, así que fichas en vez de desplegable —
   un <select> obligaría a elegir uno solo y esconde lo ya marcado. */
.pay-src {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.pay-src__chip {
  appearance: none;
  border: 1px solid $border;
  background: #f9fafc;
  color: $ink-2;
  font-family: $font;
  font-size: 13px;
  font-weight: 550;
  padding: 8px 13px;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;

  &:hover { background: #f2f4f9; }

  &.is-on {
    border-color: $brand;
    background: rgba(94, 114, 228, 0.09);
    color: $brand;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3.5px rgba(94, 114, 228, 0.12);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pay-src__chip { transition: none; }
}

.field-input--textarea {
  height: auto;
  min-height: 72px;
  resize: vertical;
  padding-top: 8px;
}

.field-input--readonly {
  background: $bg;
  color: $ink-3;
  cursor: default;
}

select.field-input {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 30px;
  cursor: pointer;
}

.field-error {
  font-size: 11px;
  color: #c62828;
  font-weight: 600;
}

.field-hint {
  font-size: 11px;
  color: $ink-3;
  line-height: 1.4;
}

.field-optional {
  font-size: 10px;
  color: $ink-4;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

// ─── Country multiselect ─────────────────────────────────────────────────────
.multiselect-wrap {
  :deep(.multiselect) { min-height: 40px; }

  :deep(.multiselect__tags) {
    min-height: 40px;
    border-radius: 10px;
    border: 1px solid $border;
    padding: 10px 38px 0 13px;
    font-size: 13.5px;
    color: $ink;
    background: #f9fafc;
    transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
  }

  :deep(.multiselect__tags:hover) { background: #f4f6fa; }
  :deep(.multiselect--active .multiselect__tags) {
    background: #fff;
    border-color: $brand;
    box-shadow: 0 0 0 3.5px rgba(94, 114, 228, 0.12);
  }
  :deep(.multiselect__input) { background: transparent; }
  // .multiselect__single trae background:#fff por defecto → caja blanca rara dentro
  // del contenedor gris. Transparente para que se integre.
  :deep(.multiselect__single) { background: transparent; padding-left: 0; }

  :deep(.multiselect__placeholder) {
    color: $ink-4;
    font-size: 13.5px;
    margin: 0;
    padding: 0;
  }

  :deep(.multiselect__single),
  :deep(.multiselect__input),
  :deep(.multiselect__option) {
    font-size: 13.5px;
    font-family: $font;
    margin: 0;
    padding: 0;
  }

  :deep(.multiselect__select) {
    height: 42px;
    right: 0;
    display: flex;
    align-items: center;
  }

  :deep(.multiselect__content-wrapper) {
    border-radius: 10px;
    border: 1px solid $border;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.14);
    margin-top: 4px;
  }

  :deep(.multiselect__option) { padding: 10px 13px; }
  :deep(.multiselect__option--highlight) { background: $brand; }
  :deep(.multiselect__option--highlight::after) { background: $brand; }
}

.multiselect-wrap--error :deep(.multiselect__tags) { border-color: #c62828; }

.country-option {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  small { color: $ink-4; font-size: 11px; }
}

// ─── Preview bar ─────────────────────────────────────────────────────────────
.preview-box {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 9px;
  background: $ink;
  color: #fff;
}

.preview-box--draft {
  background: $bg;
  color: $ink-3;
  border: 1px dashed $border;
}
.preview-box--draft .preview-box__label { color: $ink-4; opacity: 1; }
.preview-box__note {
  font-size: 12px;
  font-weight: 600;
  color: $ink-3;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  text-align: right;
}
.preview-box__est {
  font-size: 15px;
  font-weight: 800;
  color: $ink;
  font-family: "DM Mono", "Fira Mono", monospace;
  letter-spacing: 0.02em;
}
.preview-box__hint {
  font-size: 10.5px;
  font-weight: 500;
  color: $ink-4;
}

.preview-box__label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.6;
  font-weight: 700;
}

.preview-box__code {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.03em;
}

// ─── Confirmation step ───────────────────────────────────────────────────────
.confirm-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.confirm-view__intro {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 15px;
  background: #f1eefe;
  border: none;
  border-radius: 12px;
  color: $ink-2;
  font-size: 13px;

  strong { display: block; font-size: 13px; font-weight: 700; color: $ink; margin-bottom: 3px; }
  p { margin: 0; font-size: 12px; color: #475569; line-height: 1.45; }
}

.confirm-view__icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: $brand;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i { color: #fff; font-size: 14px; }
}

.confirm-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid $border;
  border-radius: 10px;
  overflow: hidden;
}

.confirm-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 15px;
  border-bottom: 1px solid $border;
  font-size: 13px;

  &:last-child { border-bottom: none; }
  &:nth-child(even) { background: $bg; }
}

.confirm-row__label {
  font-size: 11px;
  font-weight: 700;
  color: $ink-2;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  min-width: 80px;
}

.confirm-row__value {
  font-size: 13px;
  font-weight: 600;
  color: $ink;
  text-align: right;

  &--notes {
    font-size: 12px;
    color: $ink-2;
    line-height: 1.4;
    white-space: pre-wrap;
  }
}

.confirm-row__code {
  font-size: 15px;
  font-weight: 800;
  color: $brand;
  letter-spacing: 0.03em;
}

// ─── Spinners ────────────────────────────────────────────────────────────────
.btn-spinner {
  width: 13px;
  height: 13px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}

.btn-spinner--dark {
  border-color: rgba(15,23,42,0.15);
  border-top-color: $ink-2;
}

@keyframes spin { to { transform: rotate(360deg); } }

// ─── Success view ────────────────────────────────────────────────────────────
.success-view { display: flex; flex-direction: column; }

.success-view__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba($brand, 0.1);
}

.success-view__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: $green;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  i { color: #fff; font-size: 14px; }
}

.success-view__headline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.success-view__title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: $ink;
}

.success-view__meta {
  margin: 2px 0 0;
  font-size: 12px;
  color: #166534;
}

.success-view__code { font-weight: 700; }
.success-view__client { opacity: 0.8; }

.success-view__header .btn-close {
  margin-left: auto;
  background: #eef2ff;
  color: $brand;
}

.success-view__body {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.success-processing-note {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  color: $ink-3;
  background: $bg;
  border: 1px solid $border;
  border-radius: 8px;
  padding: 10px 13px;
}

.success-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.success-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  text-decoration: none;
  font-family: $font;
  transition: filter 0.12s, opacity 0.12s;
  i { font-size: 14px; flex-shrink: 0; }

  &:hover:not(:disabled):not(.success-btn--disabled) { filter: brightness(0.94); }
  &:disabled, &.success-btn--disabled { opacity: 0.5; cursor: default; }
}

.success-btn--pdf {
  background: $purple;
  color: #fff;
  box-shadow: 0 10px 24px rgba(103, 61, 230, 0.22);
}

.success-btn--whatsapp {
  background: $green;
  color: #fff;
  box-shadow: 0 10px 24px rgba(17, 177, 77, 0.2);
}

.success-btn--close {
  background: #f5f7fb;
  color: $ink-2;
}

.success-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.01em;
  &--sent       { background: rgba(17, 177, 77, 0.12); color: $green; }
  &--processing { background: #fef9c3; color: #a16207; }
  &--pending    { background: #f1f5f9; color: #475569; }
  &--error      { background: #fff1f2; color: #c62828; }
}

// ─── Responsive ──────────────────────────────────────────────────────────────
@media (max-width: 640px) {
  .modal-overlay { padding: 10px; align-items: flex-start; }
  .modal-container { max-height: calc(100vh - 20px); border-radius: 12px; }
  /* En móvil el label vuelve ARRIBA del input (label-izquierda se aprieta). */
  .field { grid-template-columns: 1fr; row-gap: 4px; }
  .field > label { padding-top: 0; }
  .client-pick { grid-template-columns: 1fr; row-gap: 4px; }
  .client-pick__label { padding-top: 0; }
  .client-pick__body { grid-column: 1; }
  .client-pick__card { align-items: flex-start; flex-wrap: wrap; }
  .client-pick__identity { min-width: calc(100% - 45px); }
  .field > .field-input,
  .field > .multiselect-wrap,
  .field > .field-hint,
  .field > .field-error { grid-column: 1; }
  .modal-footer { flex-direction: column-reverse; }
  .btn-cancel, .btn-save { width: 100%; justify-content: center; }
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
